import { useState } from 'react';
import { SETTINGS, validateSettingValue } from '@onepos/shared';
import type { SettingDef } from '@onepos/shared';
import { Alert, Badge, Button, Card, Icon, Modal, SelectField, Switch, TextAreaField, TextField, useAction } from '@onepos/ui';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { fmtDateTime } from '../../lib/format';
import { EFFECT, LEVEL, settingValueText } from '../../lib/settings';
import type { TenantDetail } from '../../lib/types';

export function SettingsTab({ data, reload }: { data: TenantDetail; reload: () => void }) {
  const me = useMe();
  const [editing, setEditing] = useState<SettingDef | null>(null);
  const current = new Map(data.settings.filter((s) => s.branch_id === null).map((s) => [s.key, s]));

  return (
    <Card title="Sozlamalar" actions={<span className="cn-muted">Restoran darajasida</span>}>
      <ul className="cn-settings">
        {(SETTINGS as readonly SettingDef[]).map((def) => {
          const cur = current.get(def.key);
          const level = LEVEL[def.level];
          return (
            <li key={def.key} className="cn-settings__row">
              <div className="cn-settings__main">
                <span className="cn-settings__label">
                  {def.label}
                  <Badge tone={level.tone}>{level.label}</Badge>
                  {cur?.locked && (
                    <Badge tone="info">
                      <Icon name="lock" /> qulf
                    </Badge>
                  )}
                </span>
                <span className="cn-settings__desc">{def.description}</span>
                <span className="cn-settings__value">
                  {settingValueText(def, cur ? cur.value : def.default)}
                  {!cur && <span className="cn-muted"> · standart</span>}
                  {cur && <span className="cn-muted"> · {fmtDateTime(cur.updated_at)}</span>}
                </span>
              </div>
              {me.role !== 'billing' && (
                <Button size="sm" onClick={() => setEditing(def)}>
                  O‘zgartirish
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {editing && (
        <SettingModal
          key={editing.key}
          def={editing}
          tenantId={data.tenant.id}
          current={current.get(editing.key)}
          onClose={() => setEditing(null)}
          onDone={reload}
        />
      )}
    </Card>
  );
}

function SettingModal({
  def,
  tenantId,
  current,
  onClose,
  onDone,
}: {
  def: SettingDef;
  tenantId: string;
  current: TenantDetail['settings'][number] | undefined;
  onClose: () => void;
  onDone: () => void;
}) {
  const [value, setValue] = useState<unknown>(current ? current.value : def.default);
  const [locked, setLocked] = useState(current?.locked ?? false);
  const [reason, setReason] = useState('');
  const invalid = validateSettingValue(def, value);
  const act = useAction(async () => {
    await api.rpc('platform_set_setting', {
      p_tenant: tenantId,
      p_key: def.key,
      p_value: value,
      p_branch: null,
      p_locked: locked,
      p_reason: reason || null,
    });
    onDone();
    onClose();
  });

  return (
    <Modal
      open
      onClose={onClose}
      busy={act.busy}
      title={def.label}
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || invalid !== null} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      <p className="cn-muted" style={{ margin: 0 }}>
        {def.description}. Kuchga kirishi: {EFFECT[def.effect]}. {LEVEL[def.level].hint}.
      </p>
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <SettingControl def={def} value={value} onChange={setValue} error={invalid ?? undefined} />
      <Switch
        label="Qulflash"
        description="Ega bu sozlamani o‘zgartira olmaydi"
        checked={locked}
        onChange={setLocked}
      />
      <TextAreaField
        label={def.level === 'tizimli' ? 'Sabab (tavsiya etiladi)' : 'Sabab (ixtiyoriy)'}
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );
}

export function SettingControl({
  def,
  value,
  onChange,
  error,
}: {
  def: SettingDef;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
}) {
  switch (def.type) {
    case 'boolean':
      return <Switch label="Yoqilgan" checked={value === true} onChange={onChange} />;
    case 'enum':
      return (
        <SelectField
          label="Qiymat"
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          options={def.options.map((o) => ({ value: o.value, label: o.label }))}
          error={error}
        />
      );
    case 'number':
      return (
        <TextField
          label={`Qiymat (${def.min}–${def.max})`}
          inputMode="decimal"
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          error={error}
        />
      );
    case 'text':
      return (
        <TextAreaField
          label="Matn"
          rows={3}
          maxLength={def.maxLength}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          error={error}
          hint={`${def.maxLength} belgigacha`}
        />
      );
    case 'time':
      return (
        <TextField
          label="Vaqt"
          type="time"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          error={error}
        />
      );
    case 'multi': {
      const list = Array.isArray(value) ? (value as string[]) : [];
      return (
        <fieldset className="cn-checks">
          <legend className="op-field__label">Tanlang</legend>
          {def.options.map((o) => (
            <label key={o.value} className="cn-check">
              <input
                type="checkbox"
                checked={list.includes(o.value)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...list, o.value] : list.filter((v) => v !== o.value))
                }
              />
              {o.label}
            </label>
          ))}
          {error && <span className="op-field__error">{error}</span>}
        </fieldset>
      );
    }
  }
}
